import React, { useState } from "react";
import { IonButton, IonRow, IonCol, IonItem, IonInput, IonCard, IonSearchbar, IonCardTitle } from "@ionic/react";
import { getOwnersByNameGSI } from "../../utilities/owner/Owner";
import { Barn, Owner } from "../../models";
import { formatShortAddress } from "../../utilities/address/FormatAddress";
import { getBarnById } from "../../utilities/barn/Barn";
import { formattedOption } from "../../utilities/owner/SelectOrCreateOwnerUtils";
import BasicModal from "../Modals/BasicModal";

interface VerifyOwnerModalProps {
    currentValue?: formattedOption | null | undefined;
    handleCancel: () => void;
    handleUseMatchedOwner: (e: formattedOption) => void;
    handleCreateNewOwner: (newOwnerName: string) => void;
}

const VerifyOwnerModal: React.FC<VerifyOwnerModalProps> = ({ currentValue, handleCancel, handleUseMatchedOwner, handleCreateNewOwner }) => {
    // Track the user's confirmed owner name, which will be used to search the DB for the owner
    const [unConfirmedOwnerName, setUnConfirmedOwnerName] = useState(currentValue?.ownerName || "");
    const [confirmedOwnerName, setConfirmedOwnerName] = useState("");
    const [isNameConfirmed, setIsNameConfirmed] = useState(false);

    // Store in state all owners that match the confirmed owner name in the DB
    const [formattedMatchedOwners, setFormattedMatchedOwners] = useState<formattedOption[] | null | undefined>();
    const [filteredFormattedMatchedOwners, setFilteredFormattedMatchedOwners] = useState<formattedOption[] | null | undefined>();
    const [searchText, setSearchText] = useState("");

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    /**
     * User confirmed the owner name
     * Search the DB by this owner name (Server-Side Filtering)
     * Only get the owner fields needed (Projection)
     */
    const handleClickConfirm = async () => {
        setError("");
        if (unConfirmedOwnerName === "") {
            setError("Please include a owner name.");
        } else {
            const formattedUnconfirmedName = unConfirmedOwnerName.trim();
            setUnConfirmedOwnerName(formattedUnconfirmedName);
            setConfirmedOwnerName(formattedUnconfirmedName);
            setIsNameConfirmed(true);
            setIsLoading(true);
            const queryResult = await getOwnersByNameGSI(formattedUnconfirmedName, true);
            if (queryResult.isSuccess) {
                const ownersFromDB = queryResult.result;
                const formattedOwners = await formatOwners(ownersFromDB);
                setFormattedMatchedOwners(formattedOwners);
                setFilteredFormattedMatchedOwners(formattedOwners);
            }
            setIsLoading(false);
        }
    };

    const formatOwners = async (owners: Owner[]) => {
        let formatted: formattedOption[] = [];
        for (let i = 0; i < owners.length; i++) {
            const owner = owners[i];
            const formattedOwner = await formatSingleOwner(owner);
            formatted.push(formattedOwner);
        }
        return formatted;
    };

    /**
     * Inside of the modal, each owner displays the owner's name, barn name and location
     * Format the owners to have proper values for each of these data items.
     * @param owner
     * @returns formattedOption
     */
    const formatSingleOwner = async (owner: Owner) => {
        const value = owner.id;
        const name = owner.name || "Unknown - No name found";
        const location = owner.address ? formatShortAddress(owner.address) || "Unknown" : owner.location || "Unknown";

        // Have to query each time for the barn name due to the way Owner.graphql is structured.
        let barnName = "Unknown";
        if (owner.barnId) {
            const barnQuery = await getBarnById(owner.barnId);
            if (barnQuery.isSuccess) {
                const ownerBarn: Barn = barnQuery.result;
                barnName = ownerBarn.name;
            }
        }

        let object: formattedOption = {
            value: value,
            label: name + " " + location + " " + barnName,
            ownerName: name,
            ownerNameLabel: name,
            barnLabel: barnName,
            locationLabel: location,
            owner: owner,
        };
        return object;
    };

    /**
     * Allow user to search the matched owners by owner name, barn, or location
     */
    const handleSearchText = (text: string) => {
        setSearchText(text);
        if (text) {
            // Filter by text
            const filtered = formattedMatchedOwners?.filter(
                (owner) =>
                    owner.ownerName.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
                    (owner.barnLabel && owner.barnLabel.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
                    (owner.locationLabel && owner.locationLabel.toLocaleLowerCase().trim().includes(text.toLocaleLowerCase().trim()))
            );
            setFilteredFormattedMatchedOwners(filtered);
        } else if (text === "" && formattedMatchedOwners) {
            // Result the filtered list to the list of all formattedMatchedOwners
            setFilteredFormattedMatchedOwners(formattedMatchedOwners);
        } else {
            setFilteredFormattedMatchedOwners([]);
        }
    };

    /**
     * User found the owner by name in the RSP Database
     * Send that owner info back to the parent component
     * And close the modal
     */
    const handleClickUseMatchedOwner = (formattedOwner: formattedOption) => {
        handleUseMatchedOwner(formattedOwner);
    };

    const handleClickCreateNewOwner = () => {
        handleCreateNewOwner(confirmedOwnerName);
    };

    return (
        <BasicModal
            isOpen={true}
            onClose={handleCancel}
            title="Verify Owner"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: handleCancel,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="text-center">
                        <h4 className="text-info font-weight-bold">{currentValue?.ownerName ? "Is this the owner's name?" : "What is the owner's name?"}</h4>
                        <IonItem color="white">
                            <IonInput
                                type="text"
                                value={unConfirmedOwnerName}
                                aria-required={true}
                                onIonChange={(e) => {
                                    setUnConfirmedOwnerName(e.detail.value!);
                                }}
                            />
                        </IonItem>
                        <IonButton className="mt-3" color="success" onClick={() => handleClickConfirm()}>
                            Confirm
                        </IonButton>
                    </IonCol>
                </IonRow>
                {isNameConfirmed && (
                    <>
                        <hr className="mb-0" />
                        {formattedMatchedOwners && formattedMatchedOwners.length > 0 ? (
                            // There are owners that match the confirmed owner name
                            // Use the filtered owners to display the options to the user and allow search
                            <>
                                {/* Keep the Search Bar and Match Info at the top level */}
                                <IonRow className="m-0">
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">
                                            {formattedMatchedOwners?.length || 0} Match{formattedMatchedOwners && formattedMatchedOwners?.length !== 1 && "es"} for{" "}
                                            {confirmedOwnerName}
                                        </h4>
                                    </IonCol>
                                </IonRow>
                                {formattedMatchedOwners && formattedMatchedOwners?.length > 3 && (
                                    <IonRow>
                                        <IonCol className="m-0 p-0">
                                            <IonSearchbar
                                                className="m-0 pr-0"
                                                value={searchText}
                                                placeholder="Find by name, location or barn"
                                                onIonChange={(e) => handleSearchText(e.detail.value!)}
                                                color="white"></IonSearchbar>
                                        </IonCol>
                                    </IonRow>
                                )}
                                {filteredFormattedMatchedOwners && filteredFormattedMatchedOwners.length > 0 ? (
                                    <IonRow className="small-scrollable bg-light">
                                        <IonCol>
                                            {filteredFormattedMatchedOwners.map((formattedOwner, index) => (
                                                <IonRow key={index}>
                                                    <IonCol className="bg-light p-0">
                                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                                            <IonRow className="ion-align-items-space-between py-0">
                                                                <IonCol sizeXs="12" sizeMd="8" className="p-0 ion-text-left">
                                                                    <IonCardTitle>
                                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{formattedOwner.ownerName}</h4>
                                                                    </IonCardTitle>
                                                                    <p className="text-white m-0">Barn: {formattedOwner.barnLabel}</p>
                                                                    <p className="text-white m-0">Location: {formattedOwner.locationLabel}</p>
                                                                </IonCol>
                                                                <IonCol sizeXs="12" sizeMd="4" className="ion-text-center align-content-center p-0">
                                                                    <IonRow className="ion-justify-content-center">
                                                                        <IonCol sizeXs="8" sizeMd="12" className="p-0">
                                                                            <IonButton
                                                                                className="py-1 m-0"
                                                                                expand="block"
                                                                                color="primary"
                                                                                onClick={() => handleClickUseMatchedOwner(formattedOwner)}>
                                                                                Select
                                                                            </IonButton>
                                                                        </IonCol>
                                                                    </IonRow>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonCard>
                                                    </IonCol>
                                                </IonRow>
                                            ))}
                                        </IonCol>
                                    </IonRow>
                                ) : (
                                    <IonRow>
                                        <IonCol>
                                            <p>There are no options that match this search.</p>
                                        </IonCol>
                                    </IonRow>
                                )}
                            </>
                        ) : (
                            <IonRow>
                                <IonCol className="text-center text-danger">
                                    <p>No matches for this owner name in the RingSide Pro database.</p>
                                </IonCol>
                            </IonRow>
                        )}
                        <hr className="m-0" />

                        <IonRow className="my-0">
                            <IonCol size="12">
                                <IonRow>
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">Can't find the owner?</h4>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="p-0">
                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                            <IonRow className="ion-align-items-space-between py-0">
                                                <IonCol sizeXs="12" sizeMd="7" className="p-0 ion-text-left">
                                                    <IonCardTitle>
                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{confirmedOwnerName}</h4>
                                                    </IonCardTitle>
                                                    <p className="text-white ion-text-wrap m-0">Barn: {currentValue?.barnLabel}</p>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="5" className="p-0 ion-text-center align-content-center">
                                                    <IonButton className="py-1 m-0" color="tertiary" onClick={() => handleClickCreateNewOwner()}>
                                                        Create New Owner
                                                    </IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            </IonCol>
                        </IonRow>
                    </>
                )}
            </>
        </BasicModal>
    );
};
export default VerifyOwnerModal;
